import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import { getUserHealthDevices, getUserById, activateUserHealthDevice, deactivateUserHealthDevice } from '../../services/users/usersService';
import { HealthDeviceResponseDto, UserDto } from '../../services/users/types';
import ConfirmDialog from '../../components/UI/ConfirmDialog';
import DeviceDetailsView from '../../components/Users/DeviceDetailsView';

const UserDevices: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const navigate = useNavigate();

  const [devices, setDevices] = useState<HealthDeviceResponseDto[]>([]);
  const [user, setUser] = useState<UserDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage] = useState(1); // setCurrentPage não está sendo usado
  // const [totalDevices, setTotalDevices] = useState(0); // Não está sendo usado
  const [pageSize] = useState(10);
  const [refreshKey, setRefreshKey] = useState(0); // Used to force refresh

  // State for activation/deactivation confirmation
  const [deviceToActivate, setDeviceToActivate] = useState<HealthDeviceResponseDto | null>(null);
  const [deviceToDeactivate, setDeviceToDeactivate] = useState<HealthDeviceResponseDto | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [actionTitle, setActionTitle] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  
  // State for device details modal
  const [selectedDevice, setSelectedDevice] = useState<HealthDeviceResponseDto | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  // Get user details
  const fetchUserDetails = useCallback(async () => {
    if (!id || !auth.isAuthenticated) return;

    try {
      const userData = await getUserById(auth.user || null, id);
      setUser(userData);
    } catch (err) {
      console.error('Error fetching user data:', err);
    }
  }, [id, auth.isAuthenticated, auth.user]);

  // Fetch devices
  const fetchDevices = useCallback(async () => {
    if (!id || !auth.isAuthenticated) {
      setError('You must be authenticated to view user devices.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const devicesData = await getUserHealthDevices(
        auth.user || null,
        id,
        true, // enabled
        currentPage,
        pageSize
      );

      setDevices(devicesData);
      // setTotalDevices(devicesData.length); // This should be replaced with actual total when API supports it
    } catch (err) {
      console.error('Error fetching health devices:', err);
      setError('Failed to fetch devices. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [id, auth.isAuthenticated, auth.user, currentPage, pageSize]);

  // Fetch data on component mount and when dependencies change
  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices, refreshKey]);

  // Get icon for device provider
  const getProviderIcon = (provider?: string) => {
    switch (provider) {
      case 'FREESTYLELIBRE':
        return 'fa-heartbeat';
      case 'GARMIN':
        return 'fa-running';
      case 'APPLE':
        return 'fa-apple';
      default:
        return 'fa-mobile-alt';
    }
  };

  // Handle viewing device details
  const handleViewDevice = (device: HealthDeviceResponseDto) => {
    setSelectedDevice(device);
    setIsDetailsModalOpen(true);
  };

  // Open confirmation dialog for activation
  const handleActivateClick = (device: HealthDeviceResponseDto) => {
    setDeviceToActivate(device);
    setDeviceToDeactivate(null);
    setActionTitle('Activate Device');
    setActionMessage(`Are you sure you want to activate the ${device.provider} device?`);
    setIsConfirmDialogOpen(true);
  };

  // Open confirmation dialog for deactivation
  const handleDeactivateClick = (device: HealthDeviceResponseDto) => {
    setDeviceToDeactivate(device);
    setDeviceToActivate(null);
    setActionTitle('Deactivate Device');
    setActionMessage(`Are you sure you want to deactivate the ${device.provider} device?`);
    setIsConfirmDialogOpen(true);
  };

  // Handle confirmation of activation/deactivation
  const handleConfirmAction = async () => {
    if (!id) return;

    setIsActionLoading(true);

    try {
      if (deviceToActivate && deviceToActivate.id) {
        await activateUserHealthDevice(auth.user || null, id, deviceToActivate.id);
        setRefreshKey(prevKey => prevKey + 1); // Force refresh after successful action
      } else if (deviceToDeactivate && deviceToDeactivate.id) {
        await deactivateUserHealthDevice(auth.user || null, id, deviceToDeactivate.id);
        setRefreshKey(prevKey => prevKey + 1); // Force refresh after successful action
      }
    } catch (err) {
      console.error('Error performing device action:', err);
      setError('Failed to perform action on device. Please try again.');
    } finally {
      setIsActionLoading(false);
      setIsConfirmDialogOpen(false);
      setDeviceToActivate(null);
      setDeviceToDeactivate(null);
    }
  };

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div>
      <PageHeader
        title={user ? `Devices for ${user.first_name || user.username}` : "User Devices"}
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Users", to: "/users" },
          { label: user?.first_name && user?.last_name 
            ? `${user.first_name} ${user.last_name}` 
            : user?.username || 'User', to: `/users/${id}` },
          { label: "Devices" }
        ]}
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate(`/users/${id}`)}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to User
          </Button>
        }
      />


      <Card>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
            {error}
            <Button
              variant="ghost"
              size="sm"
              className="ml-2"
              onClick={() => setRefreshKey(prev => prev + 1)}
            >
              Retry
            </Button>
          </div>
        ) : devices.length === 0 ? (
          <div className="text-center py-8">
            <i className="fas fa-mobile-alt text-gray-300 text-5xl mb-3"></i>
            <p className="text-gray-500">
              No health devices found for this user.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Provider
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Language
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Updated
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {devices.map((device) => (
                  <tr key={device.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <i className={`fas ${getProviderIcon(device.provider)} text-blue-600 mr-2`}></i>
                        <span className="text-sm font-medium text-gray-900">{device.provider || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${device.enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {device.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {device.language || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(device.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(device.updatedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => handleViewDevice(device)}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </Button>
                        {device.enabled ? (
                          <Button
                            variant="ghost"
                            size="xs"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDeactivateClick(device)}
                            title="Deactivate Device"
                          >
                            <i className="fas fa-ban"></i>
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="xs"
                            className="text-green-500 hover:text-green-700"
                            onClick={() => handleActivateClick(device)}
                            title="Activate Device"
                          >
                            <i className="fas fa-play"></i>
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Confirmation Dialog for Activate/Deactivate */}
      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        title={actionTitle}
        message={actionMessage}
        confirmLabel={deviceToActivate ? "Activate" : "Deactivate"}
        confirmVariant={deviceToActivate ? "primary" : "danger"}
        cancelLabel="Cancel"
        isLoading={isActionLoading}
        onConfirm={handleConfirmAction}
        onCancel={() => {
          setIsConfirmDialogOpen(false);
          setDeviceToActivate(null);
          setDeviceToDeactivate(null);
        }}
      />
      
      {/* Device Details Modal */}
      {selectedDevice && (
        <DeviceDetailsView
          device={selectedDevice}
          isOpen={isDetailsModalOpen}
          onClose={() => {
            setIsDetailsModalOpen(false);
            setSelectedDevice(null);
          }}
        />
      )}
    </div>
  );
};

export default UserDevices;